/**
 * LiteratureResultsPane — the centre column: MedQuery results as PubMed-style citations.
 *
 * Reshaped from the old Literature tab (86d3qa4fq). Behaviour is unchanged — server-side paging
 * off the TRUE corpus total, the offset-window cap, and the degraded-connector notice all work
 * exactly as before; only the presentation moved from a DataTable to citation blocks, because at
 * column width the title, journal and authors all ellipsised into nothing.
 *
 * The `available: false` envelope is a normal result, not an error: it renders an inline notice
 * and never blanks the pane. Presentational — the page owns every search param.
 * All colour via --mg-* tokens.
 */

import React from 'react';
import { Typography, Alert, Pagination } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';

import { SectionCard, EmptyState, InlineLoading } from '../../components/shared';
import ResultRow from './ResultRow';
import type { LiteratureResult, LiteratureSearchResponse } from '../../types/research/ResearchTypes';

const { Text } = Typography;

/** "Sikiric P, Seiwerth S, et al." — enough to recognise a paper, never a full author dump. */
const attributionOf = (r: LiteratureResult): string | null => {
	const authors = Array.isArray(r.meta.authors) ? r.meta.authors : [];
	if (authors.length === 0) return null;
	if (authors.length === 1) return authors[0];
	if (authors.length === 2) return `${authors[0]}, ${authors[1]}`;
	return `${authors[0]}, ${authors[1]}, et al.`;
};

/** "J Physiol · 2023 · PMID 12345678" */
const metaLineOf = (r: LiteratureResult): string | null => {
	const parts = [
		r.meta.journal ?? null,
		r.meta.pub_year != null ? String(r.meta.pub_year) : null,
		r.meta.pmid ? `PMID ${r.meta.pmid}` : null,
	].filter(Boolean) as string[];
	return parts.length > 0 ? parts.join(' · ') : null;
};

export interface LiteratureResultsPaneProps {
	query: string;
	data: LiteratureSearchResponse | undefined;
	isLoading: boolean;
	isError: boolean;
	page: number;
	size: number;
	/** Pager bound — offset paging only reaches the first ~1000 results. */
	maxOffsetPage: number;
	onPageChange: (page: number) => void;
	onOpen: (result: LiteratureResult) => void;
	selectedId?: string | null;
}

const LiteratureResultsPane: React.FC<LiteratureResultsPaneProps> = ({
	query,
	data,
	isLoading,
	isError,
	page,
	size,
	maxOffsetPage,
	onPageChange,
	onOpen,
	selectedId,
}) => {
	const total = data?.available ? data.total : 0;
	// Cap navigation to the reachable window, but keep reporting the TRUE total — we bound the
	// pager, we do not misreport the corpus.
	const reachableTotal = Math.min(total, maxOffsetPage * size);
	const beyondOffsetWindow = total > reachableTotal;
	const results = data?.available ? data.data : [];

	const body = (() => {
		if (!query) {
			return (
				<EmptyState
					icon={<FileSearchOutlined style={{ fontSize: 40, color: 'var(--mg-text-secondary)', opacity: 0.4 }} />}
					title="Search medical literature"
					description="Search peer-reviewed literature via MedQuery."
				/>
			);
		}
		if (isLoading) return <InlineLoading message="Searching literature…" />;
		if (isError) {
			return <EmptyState title="Literature search failed" description="Adjust your query and try again." />;
		}
		// Degraded connector — surface the notice, never blank the pane.
		if (data && data.available === false) {
			return (
				<div style={{ padding: 16 }}>
					<Alert
						type="warning"
						showIcon
						message="Literature search unavailable"
						description={data.message || 'The medical-literature connector is not configured for this clinic.'}
						data-testid="research-literature-unavailable"
					/>
				</div>
			);
		}
		if (results.length === 0) {
			return <EmptyState title="No results" description="No literature matches this query." />;
		}

		return (
			<>
				{results.map((r) => (
					<ResultRow
						key={r.id}
						title={r.title}
						attribution={attributionOf(r)}
						meta={metaLineOf(r)}
						snippet={r.snippet}
						selected={selectedId === r.id}
						onClick={() => onOpen(r)}
						data-testid={`research-literature-row-${r.id}`}
					/>
				))}

				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
						alignItems: 'center',
						padding: 'var(--mg-sp-4)',
					}}
				>
					<Pagination
						size="small"
						current={page}
						pageSize={size}
						total={reachableTotal}
						showSizeChanger={false}
						onChange={(p) => onPageChange(Math.min(p, maxOffsetPage))}
						data-testid="research-literature-pager"
					/>
					{beyondOffsetWindow && (
						<Text
							type="secondary"
							style={{ fontSize: 'var(--mg-fs-xs)', textAlign: 'center' }}
							data-testid="research-offset-cap-note"
						>
							Showing the first {reachableTotal.toLocaleString()} of {total.toLocaleString()} results.
							Refine your search to reach the rest.
						</Text>
					)}
				</div>
			</>
		);
	})();

	return (
		<SectionCard
			title="Literature"
			subtitle={query && data?.available ? `${total.toLocaleString()} result${total === 1 ? '' : 's'}` : undefined}
			className="mg-research-pane mg-research-pane--flush"
		>
			{body}
		</SectionCard>
	);
};

export default LiteratureResultsPane;
